import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { BETA_FEATURES } from "../lib/featureFlags";
import { useAuth } from "../lib/auth";

// Settings card listing every available beta feature with an on/off
// toggle. Only renders for level=owner; the parent (Settings.tsx)
// also gates the render, but the guard here is belt-and-suspenders
// so a stray import in another page can't accidentally expose it.
export function BetaFeaturesPanel() {
  const user = useAuth((s) => s.user);
  const isOwner = user?.level === "owner";
  const qc = useQueryClient();
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const setFlagMut = useMutation({
    mutationFn: ({ slug, enabled }: { slug: string; enabled: boolean }) =>
      api.setFeatureFlag(slug, enabled),
    // Re-fetch the agency row so the `useFeatureFlag` hooks scattered
    // throughout the app see the new state immediately.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agency"] }),
  });

  if (!isOwner) return null;
  if (BETA_FEATURES.length === 0) return null;

  const flags = agency.data?.featureFlags ?? {};

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-800">Beta features</h2>
          <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
            Owner only
          </span>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          New features in active development. Only you (the agency owner) see
          these toggles and the features they enable. Toggle off any time if a
          feature misbehaves — your existing data stays put.
        </p>
      </div>

      <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md overflow-hidden">
        {BETA_FEATURES.map((f) => {
          const on = flags[f.slug] === true;
          return (
            <li
              key={f.slug}
              className="flex items-start justify-between gap-3 px-3 py-3"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800">
                  {f.label}
                </div>
                <p className="mt-0.5 text-xs text-slate-500">{f.description}</p>
              </div>
              <label className="flex items-center gap-2 shrink-0 select-none cursor-pointer">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={setFlagMut.isPending}
                  onChange={(e) =>
                    setFlagMut.mutate({ slug: f.slug, enabled: e.target.checked })
                  }
                  className="h-4 w-4"
                />
                <span
                  className={
                    "text-[11px] font-semibold uppercase tracking-wider " +
                    (on ? "text-emerald-700" : "text-slate-400")
                  }
                >
                  {on ? "On" : "Off"}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
